import {publishOpenCopilotChat} from '@github-ui/copilot-chat-events'
import {CopilotChatIntents, type GitHubUrlReference} from '@github-ui/copilot-chat-types'
import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback} from 'react'

import {buildCopilotUpdateIssuePrompt} from '../utils/build-copilot-update-issue-prompt'

const UPDATE_ISSUE_FROM_COMMENT_BUTTON_ID = 'copilot-update-issue-from-comment-button'

interface CommentForCopilotUpdate {
  url: string
  body: string
  id: string
  repository: {
    name: string
    owner: {
      login: string
    }
  }
}

/**
 * Hook to handle updating an issue body using GitHub Copilot.
 * Extracts the issue number from the comment URL, sends analytics, and opens Copilot chat.
 */
export function useUpdateIssueFromCommentWithCopilot() {
  const {sendAnalyticsEvent} = useAnalytics()

  const updateIssueWithCopilot = useCallback(
    (comment: CommentForCopilotUpdate) => {
      // Extract issue number from comment URL (e.g., /owner/repo/issues/123#issuecomment-456)
      const issueNumberMatch = comment.url.match(/\/issues\/(\d+)/)
      const issueNumber = issueNumberMatch ? issueNumberMatch[1] : ''

      if (!issueNumber) {
        return
      }

      // Send analytics event
      sendAnalyticsEvent('analytics.click', 'COPILOT_COMMENT_UPDATE_ISSUE_BUTTON', {
        commentId: comment.id,
        issueNumber,
        repoNWO: `${comment.repository.owner.login}/${comment.repository.name}`,
      })

      // Build prompt and open Copilot chat
      const prompt = buildCopilotUpdateIssuePrompt({
        ownerLogin: comment.repository.owner.login,
        repoName: comment.repository.name,
        issueNumber,
        commentBody: comment.body,
      })

      // Build issue URL reference for Copilot chat (remove comment anchor to get issue URL)
      const issueUrlReference: GitHubUrlReference = {
        type: 'github-url',
        url: comment.url.split('#')[0] ?? '',
        title: typeof document !== 'undefined' ? document.title : undefined,
      }

      publishOpenCopilotChat({
        intent: CopilotChatIntents.conversation,
        content: prompt,
        references: [issueUrlReference],
        id: UPDATE_ISSUE_FROM_COMMENT_BUTTON_ID,
      })
    },
    [sendAnalyticsEvent],
  )

  return {updateIssueWithCopilot}
}
